import { SeeqNames } from 'generated-seeqnames/app.constants.seeqnames';
import _ from 'lodash';
import moment from 'moment-timezone';
import { useEffect } from 'react';
import { createSelector } from 'reselect';

import { useRegisterPersistedStore } from '@/stores/PersistenceProvider';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import { DEFAULT_NUM_CLUSTERS, INPUT_NAMES } from '@/tools/clusteringV2/clustering.constants';
import { BASE_TOOL_COMMON_PROPS } from '@/tools/tool.constants';
import { getToolConfigParams, sanitizeToolItem } from '@/tools/tool.utilities';
import { type ActionsFactory, type ResettableStoreState, createStore, createStoreInstance } from '@seeqdev/utils';
import type { MappedInputFeatureV1 } from '@/tools/models/templateInstance.utilities';
import type {
  BaseToolPersistedState,
  ModelRehydratePayload,
  ToolConfigParams,
  ToolConfigValue,
  ToolItem,
} from '@/tools/tool.types';

export interface ClusteringV2StoreState extends BaseToolPersistedState {
  windowStart: number;
  windowEnd: number;
  outputsCollapsed: boolean;
  modelPropertiesExpanded: boolean;
  numClusters: number;
  inputSignals: ToolItem[];
  condition?: ToolItem | null;
  restrictToCondition?: ToolItem | null;
  tableApplicationId?: string;
  instanceId?: string;
}

export interface ClusteringV2StoreActions {
  setSearchName: (name: string) => void;
  setAdvancedParametersCollapsed: (collapsed: boolean) => void;
  setOutputsCollapsed: (collapsed: boolean) => void;
  setTrainingWindow: (window: { startTime: number; endTime: number }) => void;
  setModelPropertiesExpanded: (expanded: boolean) => void;
  setNumClusters: (numClusters: number) => void;
  addInputSignal: (signal: ToolItem) => void;
  removeInputSignal: (signal: ToolItem) => void;
  setCondition: (condition: ToolItem | null | undefined) => void;
  setRestrictToCondition: (condition: ToolItem | null | undefined) => void;
  rehydrateForEdit: (payload: ModelRehydratePayload) => void;
  reset: ResettableStoreState<ClusteringV2StoreState>;
}

export type ClusteringV2DehydratedState = ClusteringV2StoreState;

const ITEM_PARAMETER_KEYS = ['inputSignals', 'condition', 'restrictToCondition'];

const initialState: ClusteringV2StoreState = Object.freeze({
  ...BASE_TOOL_COMMON_PROPS,
  windowStart: 0,
  windowEnd: 0,
  outputsCollapsed: true,
  modelPropertiesExpanded: false,
  numClusters: DEFAULT_NUM_CLUSTERS,
  inputSignals: [] as ToolItem[],
  condition: undefined as ToolItem | null | undefined,
  restrictToCondition: undefined as ToolItem | null | undefined,
  tableApplicationId: undefined as string | undefined,
  instanceId: undefined as string | undefined,
});

const clusteringV2ConfigParamsSelector = createSelector(
  [(state: ClusteringV2StoreState) => state],
  (state): ToolConfigParams => {
    const config = getToolConfigParams({
      serializedState: state as unknown as Record<string, ToolConfigValue>,
      toolType: TREND_TOOLS.CLUSTERING_V_2,
      parameterDefinitionKeys: ITEM_PARAMETER_KEYS,
      advancedParametersCollapsed: state.advancedParametersCollapsed,
    });
    return _.omit(config, ['modelPropertiesExpanded']) as ToolConfigParams;
  },
);

const clusteringV2WindowSelector = createSelector(
  [(state: ClusteringV2StoreState) => state.windowStart, (state: ClusteringV2StoreState) => state.windowEnd],
  (windowStart, windowEnd) => ({ startTime: windowStart, endTime: windowEnd }),
);

export const dehydrateClusteringV2StoreState = (state: ClusteringV2StoreState): ClusteringV2DehydratedState => state;

const clusteringV2ActionsFactory: ActionsFactory<ClusteringV2StoreState, ClusteringV2StoreActions> = (
  set,
  _get,
  resetState,
) => ({
  setSearchName: (name) =>
    set((draft) => {
      draft.name = name;
    }),

  setAdvancedParametersCollapsed: (collapsed) =>
    set((draft) => {
      draft.advancedParametersCollapsed = collapsed;
    }),

  setOutputsCollapsed: (collapsed) =>
    set((draft) => {
      draft.outputsCollapsed = collapsed;
    }),

  setTrainingWindow: (window) =>
    set((draft) => {
      draft.windowStart = window.startTime;
      draft.windowEnd = window.endTime;
    }),

  setModelPropertiesExpanded: (expanded) =>
    set((draft) => {
      draft.modelPropertiesExpanded = expanded;
    }),

  setNumClusters: (numClusters) =>
    set((draft) => {
      draft.numClusters = numClusters;
    }),

  addInputSignal: (signal) =>
    set((draft) => {
      const sanitized = sanitizeToolItem(signal);
      if (sanitized && !draft.inputSignals.some((s) => s.id === sanitized.id)) {
        draft.inputSignals = draft.inputSignals.concat(sanitized);
      }
    }),

  removeInputSignal: (signal) =>
    set((draft) => {
      draft.inputSignals = draft.inputSignals.filter((s) => s.id !== signal?.id);
    }),

  setCondition: (item) =>
    set((draft) => {
      draft.condition = sanitizeToolItem(item) as ClusteringV2StoreState['condition'];
    }),

  setRestrictToCondition: (item) =>
    set((draft) => {
      draft.restrictToCondition = sanitizeToolItem(item) as ClusteringV2StoreState['restrictToCondition'];
    }),

  rehydrateForEdit: (payload: ModelRehydratePayload) => {
    resetState();
    const inputSignals: ToolItem[] = [];
    set((draft) => {
      draft.id = payload.id;
      draft.instanceId = payload.instanceId;
      draft.tableApplicationId = payload.instanceData?.tableApplicationId;
      (payload.modelData?.inputFeatures ?? []).forEach((feature: MappedInputFeatureV1): void => {
        const inputName: string = feature.name!;
        switch (inputName) {
          case SeeqNames.Models.InstanceNameInputFeatureName:
            // TODO: CRAB-53864: Handle duplicate names more gracefully
            draft.name = payload.instanceId ? feature.inputText : `${feature.inputText} (Copy)`;
            break;
          case INPUT_NAMES.numClustersInput:
            draft.numClusters = feature.inputNumeric ?? DEFAULT_NUM_CLUSTERS;
            break;
          case INPUT_NAMES.startTimeInput: {
            const startTimeInput = JSON.parse(feature.inputText!);
            draft.windowStart = moment(startTimeInput).valueOf();
            break;
          }
          case INPUT_NAMES.endTimeInput: {
            const endTimeInput = JSON.parse(feature.inputText!);
            draft.windowEnd = moment(endTimeInput).valueOf();
            break;
          }
          case INPUT_NAMES.trainWithinConditionInput:
            if (feature.inputItem) {
              draft.condition = { id: feature.inputItem };
            }
            break;
          case INPUT_NAMES.restrictOutputToConditionInput: {
            if (feature.inputItem) {
              draft.restrictToCondition = { id: feature.inputItem };
            }
            break;
          }
          default:
            if (inputName.startsWith(INPUT_NAMES.signalInput) && feature.inputItem) {
              inputSignals.push({ id: feature.inputItem });
            }
            break;
        }
      });
      draft.inputSignals = inputSignals;
    });
  },

  reset: (overrides) => resetState(overrides),
});

const [ClusteringV2StoreProvider, useClusteringV2StoreInstance, useClusteringV2StoreSelector] = createStore(
  'ClusteringV2',
  initialState,
  clusteringV2ActionsFactory,
);

export { ClusteringV2StoreProvider };

export const useClusteringV2ToolId = () => useClusteringV2StoreSelector((state) => state.id);
export const useClusteringV2ToolName = () => useClusteringV2StoreSelector((state) => state.name);
export const useClusteringV2ToolOriginalParameters = () =>
  useClusteringV2StoreSelector((state) => state.originalParameters);
export const useClusteringV2ToolAdvancedParametersCollapsed = () =>
  useClusteringV2StoreSelector((state) => state.advancedParametersCollapsed);
export const useClusteringV2ToolOutputsCollapsed = () =>
  useClusteringV2StoreSelector((state) => state.outputsCollapsed);
export const useClusteringV2ToolConfigVersion = () => useClusteringV2StoreSelector((state) => state.configVersion);
export const useClusteringV2ToolInstanceId = () => useClusteringV2StoreSelector((state) => state.instanceId);
export const useClusteringV2ToolTableApplicationId = () =>
  useClusteringV2StoreSelector((state) => state.tableApplicationId);
export const useClusteringV2ToolWindow = () => useClusteringV2StoreSelector(clusteringV2WindowSelector);
export const useClusteringV2ToolModelPropertiesExpanded = () =>
  useClusteringV2StoreSelector((state) => state.modelPropertiesExpanded);
export const useClusteringV2ToolNumClusters = () => useClusteringV2StoreSelector((state) => state.numClusters);
export const useClusteringV2ToolInputSignals = () => useClusteringV2StoreSelector((state) => state.inputSignals);
export const useClusteringV2ToolCondition = () => useClusteringV2StoreSelector((state) => state.condition);
export const useClusteringV2ToolRestrictToCondition = () =>
  useClusteringV2StoreSelector((state) => state.restrictToCondition);
export const useClusteringV2ToolConfigParams = () => useClusteringV2StoreSelector(clusteringV2ConfigParamsSelector);

export const useClusteringV2ToolActions = () => useClusteringV2StoreInstance().actions;

/**
 * @deprecated This function is ONLY for legacy migration from Flux/Baobab stores. Prefer
 * `<TestStoreProvider>` or `<*StoreProvider>` with UI-driven assertions instead.
 */
export function createTestClusteringV2Store(overrides?: Partial<ClusteringV2StoreState>) {
  if (process.env.NODE_ENV !== 'test') {
    throw new Error('createTestClusteringV2Store() should only be used in tests.');
  }
  return createStoreInstance({ ...initialState, ...overrides }, clusteringV2ActionsFactory);
}

/**
 * @deprecated Legacy singleton bridge for non-React consumers (utility files)
 * that cannot use hooks. New code should use the hooks above and the Provider.
 */
export function legacyCreateClusteringV2Store() {
  return createStoreInstance<ClusteringV2StoreState, ClusteringV2StoreActions>(
    initialState,
    clusteringV2ActionsFactory,
  );
}

export const usePersistClusteringV2Store = () => {
  const register = useRegisterPersistedStore();
  const store = useClusteringV2StoreInstance();

  useEffect(() => {
    return register({
      storeName: 'clusteringV2Store',
      persistenceLevel: 'WORKSHEET',
      store,
      dehydrate: dehydrateClusteringV2StoreState,
    });
  }, [register, store]);
};
